import axios from 'axios';
import { getAuthToken } from './auth';
import { BASE_URL } from '$core/helpers/BASE_URL';

const DEFAULT_TIMEOUT = 10 * 1000;

const DEFAULT_HEADERS = {
  'Access-Control-Allow-Headers': 'authorization',
  'Content-Type': 'application/json;charset=utf-8',
};

const getAuthHeaders = () => ({
  ...DEFAULT_HEADERS,
  Authorization: `Bearer ${getAuthToken()}`,
});

const globalXHRErrorHandling = (error: Error) => {
  console.error(error);
};

// Shared instance for versioned API calls (BASE_URL + v1/api)
const apiInstance = axios.create({
  timeout: DEFAULT_TIMEOUT,
  headers: DEFAULT_HEADERS,
});

apiInstance.interceptors.request.use((config) => ({
  ...config,
  baseURL: `${BASE_URL()}v1/api`,
}));

apiInstance.interceptors.response.use(
  (response) => response,
  (error) => { globalXHRErrorHandling(error); return Promise.reject(error); },
);

// Shared instance for auth/session endpoints (BASE_URL only, version 0)
const sessionInstance = axios.create({
  timeout: DEFAULT_TIMEOUT,
  headers: DEFAULT_HEADERS,
});

sessionInstance.interceptors.request.use((config) => ({
  ...config,
  baseURL: BASE_URL(),
}));

sessionInstance.interceptors.response.use(
  (response) => response,
  (error) => { globalXHRErrorHandling(error); return Promise.reject(error); },
);

const httpDel = (url: string, options: any = {}) => apiInstance.delete(url, options);

const httpGet = (url: string, options: any = {}) => apiInstance.get(url, options);

const httpPatch = (url: string, data: any, options: any = {}) => apiInstance.patch(url, data, options);

const httpPost = (url: string, data: any, options: any = {}) => (
  apiInstance.post(url, data, { ...options, headers: getAuthHeaders() })
);

const httpPut = (url: string, data: any, options: any = {}) => apiInstance.put(url, data, options);

const httpLogin = (url: string, data: any, options: any = {}) => sessionInstance.post(url, data, options);

const httpLogout = (url: string, data: any, options: any = {}) => sessionInstance.post(url, data, options);

const httpSession = (url: string, _data?: any, options: any = {}) => sessionInstance.get(url, options);

const httpGetBlob = async (url: string, options: any = {}) => {
  const response = await httpGet(url, { ...options, responseType: 'arraybuffer' });
  const arrayBufferView = new Uint8Array(response.data);
  return new Blob([arrayBufferView]);
};

const httpGetObjectUrl = async (url: string, options: any = {}) => {
  const blob = await httpGetBlob(url, options);
  const urlCreator = window.URL || window.webkitURL;
  return urlCreator.createObjectURL(blob);
};

export {
  httpDel,
  httpGet,
  httpPatch,
  httpPost,
  httpLogin,
  httpLogout,
  httpPut,
  httpGetBlob,
  httpGetObjectUrl,
  httpSession,
};
